import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SEO from '../components/common/SEO';
import PageContentBlocks from '../components/common/PageContentBlocks';
import LandingAnimations from '../components/landing/LandingAnimations';
import usePageContent from '../hooks/usePageContent';
import { publicAPI } from '../services/api';

const features = [
  { icon: '📈', key: 'earn', color: 'from-emerald-500 to-teal-600' },
  { icon: '💾', key: 'save', color: 'from-blue-500 to-indigo-600' },
  { icon: '📊', key: 'invest', color: 'from-purple-500 to-pink-600' },
  { icon: '🗣️', key: 'language', color: 'from-amber-500 to-orange-600' },
];

const cards = [
  { icon: '💰', title: 'Budgeting', desc: 'Master the 50-30-20 rule' },
  { icon: '🏦', title: 'Savings', desc: 'Build your emergency fund' },
  { icon: '📈', title: 'Investing', desc: 'Start SIP with ₹500' },
  { icon: '💳', title: 'Debt Free', desc: 'Clear loans smartly' },
];

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const { blocks } = usePageContent('landing', i18n.language);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter email');
    setLoading(true);
    try {
      await publicAPI.subscribe(email);
      toast.success('Subscribed successfully! 🎉');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Subscription failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO />
      {/* Hero with backend-managed animations */}
      <section className="relative overflow-hidden bg-hero-gradient text-white min-h-[85vh] flex items-center">
        <LandingAnimations />
        <div className="absolute inset-0 opacity-20 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-brand-400 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-400 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-lg md:text-xl text-brand-200 mb-4">
            {t('hero.pause')}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-display font-extrabold mb-6">
            {t('hero.title')}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-brand-100 max-w-3xl mx-auto mb-4">
            {t('hero.subtitle')}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            {t('hero.description')}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="text-2xl font-semibold text-gold-400 mb-8">
            {t('tagline')}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/learn" className="btn-primary bg-white text-brand-800 hover:bg-brand-50">{t('hero.cta')}</Link>
            <Link to="/tips" className="btn-secondary border-white text-white hover:bg-white/10">{t('hero.ctaSecondary')}</Link>
          </motion.div>
        </div>
      </section>

      {/* Backend-managed rich content */}
      <section className="py-20 px-4 max-w-5xl mx-auto">
        <PageContentBlocks blocks={blocks} />
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-gray-50 dark:bg-gray-900/50">
        <h2 className="section-title text-center mb-12">{t('features.title')}</h2>
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              className="card text-center group hover:-translate-y-2">
              <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform`}>
                {f.icon}
              </div>
              <h3 className="font-display font-bold text-lg">{t(`features.${f.key}`)}</h3>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Learning Cards */}
      <section className="py-20 px-4 bg-gradient-to-b from-brand-50 to-white dark:from-gray-900 dark:to-gray-950">
        <h2 className="section-title text-center mb-12">{t('landing.cardsTitle')}</h2>
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              className="card bg-card-gradient cursor-pointer hover:border-brand-300">
              <span className="text-4xl">{c.icon}</span>
              <h3 className="font-bold mt-3 text-lg">{c.title}</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto card bg-hero-gradient text-white text-center">
          <h2 className="text-2xl font-display font-bold mb-2">{t('landing.newsletter')}</h2>
          <p className="text-brand-100 mb-6">{t('landing.newsletterDesc')}</p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('landing.emailPlaceholder')}
              className="input-field flex-1 text-gray-900" required />
            <button type="submit" disabled={loading} className="btn-primary bg-gold-500 hover:bg-gold-600 whitespace-nowrap">
              {loading ? '...' : t('landing.subscribe')}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
